import type { ITeamQualityRow } from '@skystone/shared';

/**
 * Sort + filter helpers shared by the two team-breakouts panels (US6).
 *
 * Both panels start from the same `teams` array but apply different rules:
 *   - Coverage panel: drop teams with null weightedCoverage; sort by coverage
 *     desc, alphabetical tie-break.
 *   - QG panel: sort by pass rate desc, alphabetical tie-break, all teams.
 */

export function compareByCoverageDesc(a: ITeamQualityRow, b: ITeamQualityRow): number {
  // Both nulls excluded upstream by `teamsForCoverage`, so we don't need to
  // handle them here — but defensively keep nulls last.
  if (a.weightedCoverage === null && b.weightedCoverage === null) {
    return a.teamName.localeCompare(b.teamName);
  }
  if (a.weightedCoverage === null) return 1;
  if (b.weightedCoverage === null) return -1;
  if (a.weightedCoverage === b.weightedCoverage) {
    return a.teamName.localeCompare(b.teamName);
  }
  return b.weightedCoverage - a.weightedCoverage;
}

export function compareByPassRateDesc(a: ITeamQualityRow, b: ITeamQualityRow): number {
  const passA = passRate(a);
  const passB = passRate(b);
  if (passA === passB) {
    return a.teamName.localeCompare(b.teamName);
  }
  // null pass rate (no qg signal) sinks to bottom
  if (passA === null) return 1;
  if (passB === null) return -1;
  return passB - passA;
}

/** Pass rate as fraction (0..1) or null when no qg signal. */
export function passRate(row: ITeamQualityRow): number | null {
  if (row.qgTotal === 0) return null;
  return row.qgPassed / row.qgTotal;
}

/** Coverage panel data: exclude null-coverage teams, sort by coverage desc. */
export function teamsForCoverage(teams: ReadonlyArray<ITeamQualityRow>): ITeamQualityRow[] {
  return teams.filter((t) => t.weightedCoverage !== null).sort(compareByCoverageDesc);
}

/** QG panel data: keep all teams, sort by pass rate desc. */
export function teamsForPassRate(teams: ReadonlyArray<ITeamQualityRow>): ITeamQualityRow[] {
  return [...teams].sort(compareByPassRateDesc);
}

/** Pass-rate colour band — matches the `cq_qg_pass_rate` tier thresholds
 *  seeded by US7 (≥80 success, ≥50 primary, ≥20 warning, else error). */
export function passRateColorClass(rate: number | null): string {
  if (rate === null) return 'text-muted-foreground';
  const pct = rate * 100;
  if (pct >= 80) return 'text-success';
  if (pct >= 50) return 'text-primary';
  if (pct >= 20) return 'text-warning';
  return 'text-error';
}
